import * as z from 'zod'

export enum AbuseCategory {
    Harassment = 'harassment',
    HateSpeech = 'hate_speech',
    SexualContent = 'sexual_content',
    SelfHarm = 'self_harm',
    Violence = 'violence',
    PromptInjection = 'prompt_injection',
    DataExtraction = 'data_extraction',
    Spam = 'spam',
    OffTopicPersistent = 'off_topic_persistent',
    Impersonation = 'impersonation',
}

export enum AbuseConfidence {
    Low = 'low',
    Medium = 'medium',
    High = 'high',
}

export enum LlmIssueCategory {
    FalseSuccess = 'false_success',
    Hallucination = 'hallucination',
    OffBrand = 'off_brand',
    DataLeak = 'data_leak',
    InappropriateTone = 'inappropriate_tone',
    RefusalLoop = 'refusal_loop',
    Repetition = 'repetition',
    FailedAction = 'failed_action',
    EmptyReply = 'empty_reply',
    StuckFlow = 'stuck_flow',
}

export enum ConversationAnalysisTrigger {
    SummaryCycle = 'summary_cycle',
    AgentRequest = 'agent_request',
    Manual = 'manual',
}

export enum UserEngagement {
    High = 'high',
    Medium = 'medium',
    Low = 'low',
    Disengaged = 'disengaged',
}

export enum UserTone {
    Polite = 'polite',
    Neutral = 'neutral',
    Impatient = 'impatient',
    Aggressive = 'aggressive',
    Abusive = 'abusive',
}

export enum Sentiment {
    Positive = 'positive',
    Neutral = 'neutral',
    Negative = 'negative',
    Frustrated = 'frustrated',
    Excited = 'excited',
}

export enum SentimentTrend {
    Improving = 'improving',
    Stable = 'stable',
    Degrading = 'degrading',
}

/** Longest abuse or LLM issue summary an analysis stores. */
export const CONVERSATION_ANALYSIS_SUMMARY_MAX_CHARS = 1000

export const ConversationAnalysisSchema = z.object({
    // --- Metadata (set programmatically, not by the LLM) ---
    analyzedAt: z.coerce.date().describe('When the analysis was generated.'),
    analysisTrigger: z
        .enum(ConversationAnalysisTrigger)
        .describe('What triggered this analysis run.'),

    // --- Sentiment & Tone ---
    sentiment: z
        .enum(Sentiment)
        .describe('Overall user sentiment in the conversation.'),
    sentimentTrend: z
        .enum(SentimentTrend)
        .describe('How the user sentiment evolved during the conversation.'),
    userTone: z
        .enum(UserTone)
        .describe('Predominant tone of the user messages.'),

    // --- Language ---
    language: z
        .string()
        .max(10)
        .describe(
            'ISO 639-1 language code of the conversation (e.g. "en", "it").',
        ),

    // --- Topics ---
    topics: z
        .array(z.string().max(100))
        .max(10)
        .describe('Main topics discussed, as short lowercase labels.'),

    // --- User Abuse Detection ---
    userAbuseDetected: z
        .boolean()
        .describe(
            'Whether user abuse was detected. If false, userAbuseCategories MUST be empty.',
        ),
    userAbuseCategories: z
        .array(z.enum(AbuseCategory))
        .default([])
        .describe(
            'Abuse categories detected. Non-empty only if userAbuseDetected is true.',
        ),
    userAbuseConfidence: z
        .enum(AbuseConfidence)
        .optional()
        .describe(
            'Confidence level of the abuse detection. Set only if userAbuseDetected is true.',
        ),
    userAbuseSummary: z
        .string()
        .max(CONVERSATION_ANALYSIS_SUMMARY_MAX_CHARS)
        .optional()
        .describe(
            'Brief description of the abusive behavior detected. Always write in English. Set only if userAbuseDetected is true.',
        ),

    // --- LLM Quality Issues ---
    llmIssueDetected: z
        .boolean()
        .describe(
            'Whether an LLM quality issue was detected. If false, llmIssueCategories MUST be empty.',
        ),
    llmIssueCategories: z
        .array(z.enum(LlmIssueCategory))
        .default([])
        .describe(
            'LLM issue categories detected. Non-empty only if llmIssueDetected is true.',
        ),
    llmIssueSummary: z
        .string()
        .max(CONVERSATION_ANALYSIS_SUMMARY_MAX_CHARS)
        .optional()
        .describe(
            'Brief description of the LLM quality issue detected. Always write in English. Set only if llmIssueDetected is true.',
        ),

    // --- Engagement ---
    userEngagement: z
        .enum(UserEngagement)
        .describe('Level of user engagement in the conversation.'),
    conversationGoalMet: z
        .boolean()
        .optional()
        .describe(
            'Whether the user seems to have achieved what they came for. Leave undefined if unclear.',
        ),

    // --- Risk Score ---
    riskScore: z
        .number()
        .int()
        .min(0)
        .max(100)
        .describe(
            'Overall risk score (0 = safe, 100 = critical). Combination of abuse severity, persistence and escalation.',
        ),
})

export type ConversationAnalysis = z.infer<typeof ConversationAnalysisSchema>
